import { RewindMode } from "@common/packets/rewindPacket";
import { ROUND_LIMITS } from "@common/platform/rounds";
import { Artillery } from "./managers/artillery";
import { GasMaskView } from "./managers/gasMaskView";
import { RewindOverlay } from "./managers/rewindOverlay";
import { TrainingHud } from "./managers/trainingHud";
import { ActionHints } from "./managers/actionHints";
import { InventoryScreen } from "./managers/inventoryScreen";
import { ReactionWheel } from "./store/reactionWheel";
import { StoreCatalog } from "./store/storeCatalog";
import { StoreMenu } from "./store/storeMenu";
import { FireChunk } from "./objects/fireChunk";
import { AtticDark } from "./managers/atticDark";
import { NightLight } from "./managers/nightLight";
import { FireEffects } from "./managers/fireEffects";
import { CombatFx } from "./managers/combatFx";
import { GoreManager } from "./managers/gore";
import { GravityFx } from "./gravity/gravityFx";
import { OctoFx } from "./octo/octoFx";
import { FrameGuard } from "./utils/frameGuard";
import { resetCanvasBlendEachFrame } from "./utils/canvasBlend";
import { limitCanvasTints } from "./utils/canvasTints";
import { softwareRendering } from "./utils/graphicsAcceleration";
import { ControlsCard } from "./managers/controlsCard";
import { Animal } from "./objects/animal";
import {
	getSavedEmblem,
	getSavedSkin,
	noteSkinCollected,
	refreshWornSkin,
} from "./emblemEditor";
import {
	InputActions,
	InventoryMessages,
	Layer,
	ObjectCategory,
	TeamMode,
	ZIndexes,
} from "@common/constants";
import { Badges, type BadgeDefinition } from "@common/definitions/badges";
import { Emotes } from "@common/definitions/emotes";
import { ArmorType } from "@common/definitions/items/armors";
import { Ammos } from "@common/definitions/items/ammos";
import { Explosions } from "@common/definitions/explosions";
import {
	type DualGunNarrowing,
	type GunDefinition,
} from "@common/definitions/items/guns";
import { type MeleeDefinition } from "@common/definitions/items/melees";
import { Skins } from "@common/definitions/items/skins";
import {
	type LootDefinition,
	type WeaponDefinition,
} from "@common/definitions/loots";
import type {
	ColorKeys,
	ModeDefinition,
	ModeName,
} from "@common/definitions/modes";
import { Modes } from "@common/definitions/modes";
import type { JoinedData } from "@common/packets/joinedPacket";
import { JoinPacket } from "@common/packets/joinPacket";
import {
	PacketType,
	type DataSplit,
	type PacketDataIn,
	type PacketDataOut,
} from "@common/packets/packet";
import { PacketStream } from "@common/packets/packetStream";
import { type UpdateDataOut } from "@common/packets/updatePacket";
import { gunSwapSlot } from "@common/utils/gunPickup";
import { CircleHitbox } from "@common/utils/hitbox";
import { adjacentOrEquivLayer, equalLayer } from "@common/utils/layer";
import { EaseFunctions, Geometry, Numeric } from "@common/utils/math";
import { FloorNames } from "@common/utils/terrain";
import { Timeout } from "@common/utils/misc";
import { DefinitionType } from "@common/utils/objectDefinitions";
import { ObjectPool } from "@common/utils/objectPool";
import { type ObjectsNetData } from "@common/utils/objectsSerializations";
import {
	random,
	randomFloat,
	randomRotation,
	randomVector,
} from "@common/utils/random";
import { Vec, type Vector } from "@common/utils/vector";
import { pickupReach } from "@common/utils/spareGear";
import { autoPickupHeld, clearAutoPickupHolds } from "./utils/autoPickupHold";
import { deckAt } from "./utils/roofs";
import { sound, type Sound } from "@pixi/sound";
import FontFaceObserver from "fontfaceobserver";
import $ from "jquery";
import { Application, Color, Container, loadTextures } from "pixi.js";
import "pixi.js/prepare";
import { setUpCommands } from "./console/commands";
import { GameConsole } from "./console/gameConsole";
import { defaultClientCVars } from "./console/variables";
import { CameraManager } from "./managers/cameraManager";
import { DamageNumbers } from "./managers/damageNumbers";
import { CombatFeedback } from "./managers/combatFeedback";
import { WarAudio } from "./war/warAudio";
import { WarLook } from "./war/warLook";
import { WarSky } from "./war/warSky";
import { WarWeather } from "./war/warWeather";
import { TrenchHud } from "./managers/trenchHud";
import {
	EmoteWheelManager,
	MapPingWheelManager,
} from "./managers/emoteWheelManager";
import { GasManager, GasRender } from "./managers/gasManager";
import { HazardRender } from "./managers/hazardRender";
import { SnowLook } from "./managers/snowLook";
import { StormView } from "./managers/stormView";
import { NightCreep } from "./managers/nightCreep";
import { InputManager } from "./managers/inputManager";
import { JumpInView } from "./managers/jumpIn";
import { BloodTrailHud, BloodTrailMarks } from "./managers/bloodTrails";
import { MapManager } from "./managers/mapManager";
import { ParticleManager } from "./managers/particleManager";
import { ScreenRecordManager } from "./managers/screenRecordManager";
import { GameSound, SoundManager } from "./managers/soundManager";
import { UIManager } from "./managers/uiManager";
import { Building } from "./objects/building";
import { Bullet } from "./objects/bullet";
import { BulletTargets } from "./objects/bulletTargets";
import { DeathMarker } from "./objects/deathMarker";
import { Decal } from "./objects/decal";
import { explosion } from "./objects/explosion";
import { type GameObject } from "./objects/gameObject";
import { Loot } from "./objects/loot";
import { Obstacle } from "./objects/obstacle";
import { Parachute } from "./objects/parachute";
import { Plane } from "./objects/plane";
import { Player } from "./objects/player";
import { Projectile } from "./objects/projectile";
import { SyncedParticle } from "./objects/syncedParticle";
import {
	autoPickup,
	fetchServerData,
	finalizeUI,
	requestReconnectAdmission,
	resetPlayButtons,
	setUpUI,
	teamSocket,
	unlockPlayButtons,
	updateDisconnectTime,
} from "./ui";
import {
	EMOTE_SLOTS,
	LAYER_TRANSITION_DELAY,
	PERK_MESSAGE_FADE_TIME,
	PIXI_SCALE,
	UI_DEBUG_MODE,
} from "./utils/constants";
import { DebugRenderer } from "./utils/debugRenderer";
import { setUpNetGraph } from "./utils/graph/netGraph";
import { loadSpritesheets, TrenchioSprite } from "./utils/pixi";
import { t, tr } from "./utils/translations/phrases";
import { translate, initTranslation } from "./utils/translations/translations";
import { type TranslationKeys } from "./utils/translations/typings";
import { Tween, type TweenOptions } from "./utils/tween";
import { colord } from "colord";
import type { DebugMenu } from "./utils/debugMenu";
import { LagSmoothing } from "./managers/lagSmoothing";
import { DamageMarks } from "./managers/damageMarks";
import { LightFans } from "./managers/lightFans";
import { Lighting } from "./managers/lighting";
import { NormalLook } from "./managers/normalLook";
import { ShadowLook } from "./managers/shadowLook";

interface ObjectClassMapping {
	readonly [ObjectCategory.Player]: typeof Player;
	readonly [ObjectCategory.Obstacle]: typeof Obstacle;
	readonly [ObjectCategory.DeathMarker]: typeof DeathMarker;
	readonly [ObjectCategory.Loot]: typeof Loot;
	readonly [ObjectCategory.Building]: typeof Building;
	readonly [ObjectCategory.Decal]: typeof Decal;
	readonly [ObjectCategory.Parachute]: typeof Parachute;
	readonly [ObjectCategory.Projectile]: typeof Projectile;
	readonly [ObjectCategory.SyncedParticle]: typeof SyncedParticle;
	readonly [ObjectCategory.Animal]: typeof Animal;
	readonly [ObjectCategory.Fire]: typeof FireChunk;
}

const ObjectClassMapping: ObjectClassMapping = Object.freeze({
	[ObjectCategory.Player]: Player,
	[ObjectCategory.Obstacle]: Obstacle,
	[ObjectCategory.DeathMarker]: DeathMarker,
	[ObjectCategory.Loot]: Loot,
	[ObjectCategory.Building]: Building,
	[ObjectCategory.Decal]: Decal,
	[ObjectCategory.Parachute]: Parachute,
	[ObjectCategory.Projectile]: Projectile,
	[ObjectCategory.SyncedParticle]: SyncedParticle,
	[ObjectCategory.Animal]: Animal,
	[ObjectCategory.Fire]: FireChunk,
} satisfies {
	readonly [K in ObjectCategory]: new (
		id: number,
		data: ObjectsNetData[K],
	) => InstanceType<ObjectClassMapping[K]>;
});

type ObjectMapping = {
	readonly [Cat in keyof ObjectClassMapping]: InstanceType<
		ObjectClassMapping[Cat]
	>;
};

type Colors = Record<ColorKeys | "background" | "ghillie", Color>;

type ComparisonTrend = "better" | "worse" | "neutral";

interface WeaponComparisonLine {
	readonly label: string;
	current: string;
	next: string;
	trend: ComparisonTrend;
}

interface CondensedComparisonLine {
	readonly label: string;
	readonly value: string;
	trend: ComparisonTrend;
}

interface WeaponComparisonPayload {
	readonly key: string;
	readonly lines: WeaponComparisonLine[];
	readonly mode: "compare" | "inspect";
	readonly condensedLines?: CondensedComparisonLine[];
}

interface CondensedDiffInput {
	readonly label: string;
	readonly current?: number;
	readonly next?: number;
	readonly higherIsBetter?: boolean;
	readonly formatter?: (value: number) => string;
}

interface WeaponComparisonBuilder<T extends WeaponDefinition> {
	readonly compare: (current: T, next: T) => WeaponComparisonLine[];
	readonly condensedCompare: (current: T, next: T) => CondensedComparisonLine[];
	readonly condensedInspect: (definition: T) => CondensedComparisonLine[];
}

const COMPARISON_EPSILON = 1e-3;

const fmtNum = (v: number, d = 2) => {
	const fixed = v.toFixed(d);
	return fixed.replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
};
const fmtSec = (s: number) => `${fmtNum(s, 2)}s`;
const fmtDeg = (v: number) => `${fmtNum(v, 2)}°`;
const fmtMult = (v: number) => `${fmtNum(v, 2)}x`;
const fmtRate = (v: number) => `${fmtNum(v, 2)}/s`;
const fmtOptional = (v: number | undefined, f: (n: number) => string) =>
	v === undefined ? "—" : f(v);

function determineTrend(
	current?: number,
	next?: number,
	higherIsBetter = true,
): ComparisonTrend {
	if (current === undefined || next === undefined) return "neutral";
	const delta = next - current;
	if (Math.abs(delta) < COMPARISON_EPSILON) return "neutral";
	const improved = higherIsBetter ? delta > 0 : delta < 0;
	return improved ? "better" : "worse";
}

function createComparisonLine(
	label: string,
	current?: number,
	next?: number,
	higherIsBetter = true,
	formatter: (value: number) => string = (v) => fmtNum(v, 2),
): WeaponComparisonLine | undefined {
	if (current === undefined && next === undefined) return undefined;
	return {
		label,
		current: current === undefined ? "—" : formatter(current),
		next: next === undefined ? "—" : formatter(next),
		trend: determineTrend(current, next, higherIsBetter),
	};
}

function createCondensedDiffLine(
	label: string,
	current?: number,
	next?: number,
	higherIsBetter = true,
	formatter: (value: number) => string = (v) => fmtNum(v, 2),
): CondensedComparisonLine | undefined {
	if (current === undefined || next === undefined) return undefined;
	const trend = determineTrend(current, next, higherIsBetter);
	const delta = Math.abs(next - current);
	const magnitude = formatter(trend === "neutral" ? 0 : delta);
	const sign = trend === "neutral" ? "=" : trend === "better" ? "+" : "-";
	return { label, value: `${sign}${magnitude}`, trend };
}

function buildCondensedDiffLines(
	inputs: CondensedDiffInput[],
): CondensedComparisonLine[] {
	return inputs
		.map(({ label, current, next, higherIsBetter, formatter }) =>
			createCondensedDiffLine(label, current, next, higherIsBetter, formatter),
		)
		.filter(Boolean) as CondensedComparisonLine[];
}

const createCondensedValueLine = (
	label: string,
	value: string,
): CondensedComparisonLine => ({
	label,
	value,
	trend: "neutral",
});

function buildSpecialCondensedLine(
	label: string,
	currentTags: string[],
	nextTags: string[],
): CondensedComparisonLine | undefined {
	if (!currentTags.length && !nextTags.length) return undefined;

	const nextExtras = nextTags.filter((tag) => !currentTags.includes(tag));
	const currentExtras = currentTags.filter((tag) => !nextTags.includes(tag));

	let trend: ComparisonTrend = "neutral";
	if (nextExtras.length && !currentExtras.length) trend = "better";
	else if (!nextExtras.length && currentExtras.length) trend = "worse";

	const displayValue = nextTags.length
		? formatTagList(nextTags)
		: formatTagList(currentTags);
	return { label, value: displayValue, trend };
}

function getGunDamage(def: GunDefinition): number {
	return def.ballistics.damage * (def.bulletCount ?? 1);
}

function getExplosionDamage(def: GunDefinition): number | undefined {
	const ref = def.ballistics.onHitExplosion;
	if (!ref) return undefined;
	return Explosions.fromStringSafe(ref)?.damage;
}

function getGunReloadTime(def: GunDefinition): number | undefined {
	return def.reloadFullOnEmpty ? def.fullReloadTime : def.reloadTime;
}

function getGunFireRate(def: GunDefinition): number | undefined {
	if (def.fireDelay <= 0) return undefined;
	return 1000 / def.fireDelay;
}

function getGunDPS(def: GunDefinition): number | undefined {
	const rate = getGunFireRate(def);
	return rate === undefined ? undefined : getGunDamage(def) * rate;
}

function getGunTotalDPS(def: GunDefinition): number | undefined {
	const rate = getGunFireRate(def);
	if (rate === undefined) return undefined;
	const explode = getExplosionDamage(def) ?? 0;
	return (getGunDamage(def) + explode) * rate;
}

function getGunCondensedLines(
	current: GunDefinition,
	next: GunDefinition,
): CondensedComparisonLine[] {
	const explosionCurrent = getExplosionDamage(current) ?? 0;
	const explosionNext = getExplosionDamage(next) ?? 0;

	const lines = buildCondensedDiffLines([
		{
			label: t("DPS"),
			current: getGunTotalDPS(current),
			next: getGunTotalDPS(next),
			formatter: (v) => fmtNum(v, 1),
		},
		{
			label: t("Reload"),
			current: getGunReloadTime(current),
			next: getGunReloadTime(next),
			higherIsBetter: false,
			formatter: fmtSec,
		},
		{
			label: t("Spread"),
			current: current.shotSpread,
			next: next.shotSpread,
			higherIsBetter: false,
			formatter: fmtDeg,
		},
	]);

	if (explosionCurrent || explosionNext) {
		const dpsLine = lines.find((l) => l.label === t("DPS"));
		if (dpsLine) dpsLine.trend = "neutral";
	}

	const specialCondensed = buildSpecialCondensedLine(
		t("Special"),
		getGunSpecialTags(current),
		getGunSpecialTags(next),
	);
	if (specialCondensed) lines.push(specialCondensed);

	return lines;
}

function getGunCondensedInspectLines(
	def: GunDefinition,
): CondensedComparisonLine[] {
	const result = [
		createCondensedValueLine(
			t("DPS"),
			fmtOptional(getGunTotalDPS(def), (v) => fmtNum(v, 1)),
		),
		createCondensedValueLine(
			t("Reload"),
			fmtOptional(getGunReloadTime(def), fmtSec),
		),
		createCondensedValueLine(t("Spread"), fmtDeg(def.shotSpread)),
	];
	const specialTags = getGunSpecialTags(def);
	if (specialTags.length) {
		result.push({
			label: t("Special"),
			value: formatTagList(specialTags),
			trend: "neutral",
		});
	}
	return result;
}

function getMeleeCondensedLines(
	current: MeleeDefinition,
	next: MeleeDefinition,
): CondensedComparisonLine[] {
	const lines = buildCondensedDiffLines([
		{
			label: t("Damage"),
			current: current.damage,
			next: next.damage,
			formatter: (v) => fmtNum(v, 0),
		},
		{
			label: t("Cooldown"),
			current: current.cooldown,
			next: next.cooldown,
			higherIsBetter: false,
			formatter: (v) => fmtSec(v / 1000),
		},
	]);

	const specialCondensed = buildSpecialCondensedLine(
		t("Special"),
		current.specialTags ?? [],
		next.specialTags ?? [],
	);
	if (specialCondensed) lines.push(specialCondensed);
	return lines;
}

function getMeleeCondensedInspectLines(
	def: MeleeDefinition,
): CondensedComparisonLine[] {
	const result = [
		createCondensedValueLine(t("Damage"), fmtNum(def.damage, 0)),
		createCondensedValueLine(t("Cooldown"), fmtSec(def.cooldown / 1000)),
	];
	const tags = def.specialTags ?? [];
	if (tags.length)
		result.push(createCondensedValueLine(t("Special"), formatTagList(tags)));
	return result;
}

function formatDamageValue(base?: number, explosion?: number): string {
	if (base === undefined) return "—";
	const baseText = fmtNum(base, 1);
	if (!explosion) return baseText;
	return `${baseText} (+${fmtNum(explosion, 1)})`;
}

function formatDpsValue(base?: number, total?: number): string {
	if (base === undefined) return "—";
	const baseText = fmtNum(base, 1);
	if (total === undefined || Math.abs(total - base) < COMPARISON_EPSILON)
		return baseText;
	return `${baseText} (${fmtNum(total, 1)})`;
}

function getGunSpecialTags(def: GunDefinition): string[] {
	const tags = new Set<string>();
	const { ballistics } = def;

	if (ballistics.onHitExplosion) tags.add(t("Explosive"));
	if (ballistics.shrapnel) tags.add(t("Shrapnel"));
	if (ballistics.enemySpeedMultiplier) tags.add(t("Slow"));
	if (ballistics.teammateHeal) tags.add(t("Heals"));
	if (ballistics.removePerk) tags.add(t("Perk Breaker"));
	if (def.summonAirdrop) tags.add(t("Airdrop"));
	if (def.infiniteAmmo) tags.add(t("Inf Ammo"));
	if (def.consistentPatterning) tags.add(t("Consistent Patterning"));
	const ammoDef = Ammos.fromStringSafe(def.ammoType);
	if (ammoDef?.ephemeral) tags.add(t("Inf Ammo"));

	return [...tags];
}

function formatTagList(tags: string[], perLine = 2): string {
	if (!tags.length) return t("None");
	const lines: string[] = [];
	for (let i = 0; i < tags.length; i += perLine) {
		lines.push(tags.slice(i, i + perLine).join(", "));
	}
	return lines.join("\n");
}

function createSpecialComparisonLine(
	label: string,
	current: string[],
	next: string[],
): WeaponComparisonLine | undefined {
	if (!current.length && !next.length) return undefined;

	const nextExtras = next.filter((t) => !current.includes(t));
	const currentExtras = current.filter((t) => !next.includes(t));

	let trend: ComparisonTrend = "neutral";
	if (nextExtras.length && !currentExtras.length) trend = "better";
	else if (!nextExtras.length && currentExtras.length) trend = "worse";

	return {
		label,
		current: formatTagList(current),
		next: formatTagList(next),
		trend,
	};
}

function compareGunDefinitions(
	current: GunDefinition,
	next: GunDefinition,
): WeaponComparisonLine[] {
	const reloadCurrent = getGunReloadTime(current);
	const reloadNext = getGunReloadTime(next);
	const currentSpecial = getGunSpecialTags(current);
	const nextSpecial = getGunSpecialTags(next);
	const currentExplosionDamage = getExplosionDamage(current);
	const nextExplosionDamage = getExplosionDamage(next);
	const currentTotalDps = getGunTotalDPS(current);
	const nextTotalDps = getGunTotalDPS(next);

	const lines = [
		createComparisonLine(
			t("Damage"),
			getGunDamage(current),
			getGunDamage(next),
			true,
			(v) => fmtNum(v, 1),
		),
		createComparisonLine(
			t("DPS"),
			getGunDPS(current),
			getGunDPS(next),
			true,
			(v) => fmtNum(v, 1),
		),
		createComparisonLine(
			t("Fire Rate"),
			getGunFireRate(current),
			getGunFireRate(next),
			true,
			fmtRate,
		),
		createComparisonLine(t("Reload"), reloadCurrent, reloadNext, false, fmtSec),
		createComparisonLine(
			t("Spread"),
			current.shotSpread,
			next.shotSpread,
			false,
			fmtDeg,
		),
		createComparisonLine(
			t("Move Spread"),
			current.moveSpread,
			next.moveSpread,
			false,
			fmtDeg,
		),
		createComparisonLine(
			t("Move Speed"),
			current.speedMultiplier,
			next.speedMultiplier,
			true,
			fmtMult,
		),
		createComparisonLine(
			t("Range"),
			current.ballistics.range,
			next.ballistics.range,
			true,
			(v) => fmtNum(v, 0),
		),
		createComparisonLine(
			t("Capacity"),
			current.capacity,
			next.capacity,
			true,
			(v) => fmtNum(v, 0),
		),
		createSpecialComparisonLine(t("Special"), currentSpecial, nextSpecial),
	].filter(Boolean) as WeaponComparisonLine[];

	const damageLine = lines.find((l) => l.label === t("Damage"));
	if (damageLine) {
		damageLine.current = formatDamageValue(
			getGunDamage(current),
			currentExplosionDamage,
		);
		damageLine.next = formatDamageValue(
			getGunDamage(next),
			nextExplosionDamage,
		);
		if (currentExplosionDamage || nextExplosionDamage)
			damageLine.trend = "neutral";
	}

	const dpsLine = lines.find((l) => l.label === t("DPS"));
	if (dpsLine) {
		dpsLine.current = formatDpsValue(getGunDPS(current), currentTotalDps);
		dpsLine.next = formatDpsValue(getGunDPS(next), nextTotalDps);
		if (currentExplosionDamage || nextExplosionDamage)
			dpsLine.trend = "neutral";
	}

	return lines;
}

function compareMeleeDefinitions(
	current: MeleeDefinition,
	next: MeleeDefinition,
): WeaponComparisonLine[] {
	return [
		createComparisonLine(t("Damage"), current.damage, next.damage, true, (v) =>
			fmtNum(v, 0),
		),
		createComparisonLine(
			t("Cooldown"),
			current.cooldown,
			next.cooldown,
			false,
			(v) => fmtSec(v / 1000),
		),
		createComparisonLine(
			t("Obstacle\nDMG Multi"),
			current.obstacleMultiplier,
			next.obstacleMultiplier,
			true,
			(v) => fmtNum(v, 2),
		),
		createComparisonLine(t("Reach"), current.radius, next.radius, true, (v) =>
			fmtNum(v, 2),
		),
		createComparisonLine(
			t("Speed"),
			current.speedMultiplier,
			next.speedMultiplier,
			true,
			(v) => fmtNum(v, 2),
		),
	].filter(Boolean) as WeaponComparisonLine[];
}

function convertToInspectionLines(
	lines: WeaponComparisonLine[],
): WeaponComparisonLine[] {
	return lines.map(({ label, next }) => ({
		label,
		current: "",
		next,
		trend: "neutral",
	}));
}

function buildWeaponComparisonPayload<T extends WeaponDefinition>(
	loot: T,
	current: T | undefined,
	condensedEnabled: boolean,
	builder: WeaponComparisonBuilder<T>,
): WeaponComparisonPayload {
	const comparing = current !== undefined;
	const lines = builder.compare(current ?? loot, loot);
	const mode: "compare" | "inspect" = comparing ? "compare" : "inspect";

	return {
		key: `${condensedEnabled ? "condensed" : "full"}:${comparing ? `${current.idString}->` : ""}${loot.idString}`,
		lines: comparing ? lines : convertToInspectionLines(lines),
		mode,
		condensedLines: condensedEnabled
			? comparing
				? builder.condensedCompare(current, loot)
				: builder.condensedInspect(loot)
			: undefined,
	};
}

function getWeaponComparisonPayload(
	lootDefinition: LootDefinition | undefined,
	currentWeapon: WeaponDefinition | undefined,
	enabled: boolean,
	condensedEnabled: boolean,
): WeaponComparisonPayload | undefined {
	if (!enabled || !lootDefinition) return undefined;
	if (
		lootDefinition.defType !== DefinitionType.Gun &&
		lootDefinition.defType !== DefinitionType.Melee
	)
		return undefined;

	const current =
		currentWeapon?.defType === lootDefinition.defType
			? currentWeapon
			: undefined;

	const payload =
		lootDefinition.defType === DefinitionType.Gun
			? buildWeaponComparisonPayload(
					lootDefinition as GunDefinition,
					current as GunDefinition | undefined,
					condensedEnabled,
					{
						compare: compareGunDefinitions,
						condensedCompare: getGunCondensedLines,
						condensedInspect: getGunCondensedInspectLines,
					},
				)
			: buildWeaponComparisonPayload(
					lootDefinition as MeleeDefinition,
					current as MeleeDefinition | undefined,
					condensedEnabled,
					{
						compare: compareMeleeDefinitions,
						condensedCompare: getMeleeCondensedLines,
						condensedInspect: getMeleeCondensedInspectLines,
					},
				);

	return payload.lines.length ? payload : undefined;
}

let lastRenderedComparisonKey: string | undefined;

function renderWeaponComparison(payload?: WeaponComparisonPayload): void {
	const { weaponComparisonList, interactMsg } = UIManager.ui;
	const payloadKey = payload ? `${payload.mode}:${payload.key}` : undefined;

	if (payloadKey === lastRenderedComparisonKey) return;
	lastRenderedComparisonKey = payloadKey;

	if (!payload || (!payload.lines.length && !payload.condensedLines?.length)) {
		interactMsg.removeClass("has-comparison inspect-mode");
		weaponComparisonList.empty();
		return;
	}

	const { lines, mode, condensedLines } = payload;

	interactMsg.addClass("has-comparison");
	interactMsg.toggleClass("inspect-mode", mode === "inspect");
	weaponComparisonList.empty();

	if (condensedLines?.length) {
		for (const line of condensedLines) {
			const row = $("<div>").addClass(
				"weapon-comparison-line weapon-comparison-line--condensed",
			);
			$("<span>")
				.addClass("weapon-comparison-label")
				.text(line.label)
				.appendTo(row);

			const values = $("<div>")
				.addClass(
					"weapon-comparison-values weapon-comparison-values--condensed",
				)
				.appendTo(row);

			$("<span>")
				.addClass(
					`weapon-comparison-diff weapon-comparison-diff--${
						mode === "compare" ? line.trend : "neutral"
					}${mode === "inspect" ? " weapon-comparison-diff--inspect" : ""}`,
				)
				.text(line.value)
				.appendTo(values);

			weaponComparisonList.append(row);
		}
		return;
	}

	for (const line of lines) {
		const row = $("<div>").addClass("weapon-comparison-line");
		$("<span>")
			.addClass("weapon-comparison-label")
			.text(line.label)
			.appendTo(row);

		const values = $("<div>")
			.addClass("weapon-comparison-values")
			.appendTo(row);

		if (mode === "inspect") {
			values.addClass("weapon-comparison-values--inspect");
			$("<span>")
				.addClass("weapon-comparison-new")
				.text(line.next ?? "—")
				.appendTo(values);
		} else {
			$("<span>")
				.addClass("weapon-comparison-original")
				.text(line.current ?? "—")
				.appendTo(values);
			$("<span>")
				.addClass(
					`weapon-comparison-arrow weapon-comparison-arrow--${line.trend}`,
				)
				.text("→")
				.appendTo(values);
			$("<span>")
				.addClass("weapon-comparison-new")
				.text(line.next ?? "—")
				.appendTo(values);
		}

		weaponComparisonList.append(row);
	}
}

/** a message's last updates whose voice reactions are said (a late tick's two or three; a catch-up's seconds are not) */
const MESSAGE_REACTION_UPDATES = 4;

export const Game = new (class Game {
	private _socket?: WebSocket;
	private _connectionAddress?: string;
	private _reconnectUntil = 0;
	private _reconnectRequestPending = false;
	private _reconnectTimer?: ReturnType<typeof setTimeout>;

	socketCloseCallback?: (value: unknown) => void;

	readonly objects = new ObjectPool<ObjectMapping>();
	readonly bullets = new Set<Bullet>();
	readonly planes = new Set<Plane>();

	ambience?: GameSound;
	riverAmbience!: GameSound;
	oceanAmbience!: GameSound;

	layerTween?: Tween<Container>;

	readonly spinningImages = new Map<TrenchioSprite, number>();

	readonly playerNames = new Map<
		number,
		{
			readonly name: string;
			readonly hasColor: boolean;
			readonly nameColor: Color;
			readonly badge?: BadgeDefinition;
		}
	>();

	activePlayerID = -1;
	teamID = -1;

	isTeamMode = false;

	debugMenu?: DebugMenu;

	_modeName: ModeName | undefined;
	get modeName(): ModeName {
		if (this._modeName === undefined)
			throw new Error("modeName accessed before initialization");
		return this._modeName;
	}

	set modeName(modeName: ModeName) {
		this._modeName = modeName;
		this._mode = Modes[this.modeName];

		// Converts the strings in the mode definition to Color objects
		this._colors = (
			Object.entries(this.mode.colors) as [ColorKeys, string][]
		).reduce((result, [key, color]) => {
			result[key] = new Color(color);
			return result;
		}, {} as Colors);

		// pixi filters don't apply to the background, so we have to apply them here separately
		const canvasFilters = this.mode.canvasFilters;
		if (canvasFilters) {
			const { h, s, l } = colord(this.colors.grass.toRgbaString()).toHsl();
			this.colors.background = new Color(
				`hsl(${h}, ${s * canvasFilters.saturation}%, ${l * canvasFilters.brightness}%)`,
			);
		} else {
			this.colors.background = this.colors.grass;
		}

		this._colors.ghillie = new Color(this._colors.grass).multiply(
			"hsl(0, 0%, 99%)",
		);
	}

	_mode: ModeDefinition | undefined;
	get mode(): ModeDefinition {
		if (!this._mode) throw new Error("mode accessed before initialization");
		return this._mode;
	}

	private _colors: Colors | undefined;
	get colors(): Colors {
		if (!this._colors) throw new Error("colors accessed before initialization");
		return this._colors;
	}

	/**
	 * proxy for `activePlayer`'s layer
	 */
	get layer(): Layer {
		return this.activePlayer?.layer ?? Layer.Ground;
	}

	get activePlayer(): Player | undefined {
		return this.objects.get(this.activePlayerID) as Player;
	}

	connecting = false;
	gameStarted = false;
	/** this game is the Training Range (joined from the menu's Training Range button) */
	isTraining = false;
	gameOver = false;
	spectating = false;
	error = false;

	hideSecondFloor = false;

	/** the player watched stands on a roof's deck (1.08: `BuildingDefinition.openAir`): the roofs under it stay */
	onDeck = false;

	readonly pixi = new Application();

	gasRender!: GasRender;

	readonly netGraph = setUpNetGraph();

	readonly fontObserver = new FontFaceObserver("Inter", { weight: 600 }).load();

	music!: Sound;

	readonly tweens = new Set<Tween<object>>();

	private readonly _timeouts = new Set<Timeout>();

	addTimeout(callback: () => void, delay?: number): Timeout {
		const timeout = new Timeout(callback, Date.now() + (delay ?? 0));
		this._timeouts.add(timeout);
		return timeout;
	}

	private _initialized = false;
	async init(): Promise<void> {
		if (this._initialized) {
			throw new Error("'Game' has already been initialized.");
		}
		this._initialized = true;

		GameConsole.init();
		setUpCommands();
		await initTranslation();
		InputManager.init();
		if (DEBUG_CLIENT) {
			this.debugMenu = new (await import("./utils/debugMenu")).DebugMenu();
			this.debugMenu.init();
		}
		await setUpUI();
		TrenchHud.init();
		await fetchServerData();
		this.gasRender = new GasRender(PIXI_SCALE);
		MapManager.init();
		CameraManager.init();
		GasManager.init();

		const initPixi = async (): Promise<void> => {
			const renderMode = GameConsole.getBuiltInCVar("cv_renderer");
			const renderRes = GameConsole.getBuiltInCVar("cv_renderer_res");

			const pixi = this.pixi;
			// drawn without the graphics card (acceleration off in the browser): the lightest way, every pixel drawn by
			// the processor (no antialiasing, one pixel per screen pixel on a sharp screen; utils/graphicsAcceleration.ts)
			const software = softwareRendering();
			await pixi.init({
				resizeTo: window,
				background: this.colors.background,
				// with the shader look, the world is drawn through a full-screen pass that Pixi never antialiases:
				// antialiasing the canvas as well would only cost time
				antialias:
					(InputManager.isMobile
						? GameConsole.getBuiltInCVar("mb_antialias")
						: GameConsole.getBuiltInCVar("cv_antialias")) &&
					!WarLook.shadersWanted(renderMode) &&
					!software,
				autoDensity: true,
				preferWebGLVersion: renderMode === "webgl1" ? 1 : 2,
				preference: renderMode === "webgpu" ? "webgpu" : "webgl",
				resolution:
					renderRes === "auto"
						? software
							? Math.min(window.devicePixelRatio || 1, 1)
							: window.devicePixelRatio || 1
						: parseFloat(renderRes),
				hello: true,
				autoStart: false,
				canvas: document.getElementById("game-canvas") as HTMLCanvasElement,
				// we only use pixi click events (to spectate players on click)
				// so other events can be disabled for performance
				eventFeatures: {
					move: false,
					globalMove: false,
					wheel: false,
					click: true,
				},
			});

			// (drawn with Canvas 2D, without WebGL: every frame starts drawn normally, see utils/canvasBlend.ts; and the
			// tinted copies of pictures it makes are kept to the last few hundred, utils/canvasTints.ts)
			resetCanvasBlendEachFrame(pixi.renderer);
			limitCanvasTints(pixi.renderer);

			// Setting preferCreateImageBitmap to false reduces RAM usage in most browsers
			if (
				!GameConsole.getBuiltInCVar("cv_alt_texture_loading") &&
				loadTextures.config
			) {
				loadTextures.config.preferCreateImageBitmap = false;
			}

			void loadSpritesheets(
				this.modeName,
				pixi.renderer,
				InputManager.isMobile
					? GameConsole.getBuiltInCVar("mb_high_res_textures")
					: GameConsole.getBuiltInCVar("cv_high_res_textures"),
			).then(() => {
				EmoteWheelManager.init();
				MapPingWheelManager.init();
				MapPingWheelManager.setupSlots();
			});

			// HACK: the game ui covers the canvas
			// so send pointer events manually to make clicking to spectate players work
			UIManager.ui.gameUi[0].addEventListener("pointerdown", (e) => {
				pixi.canvas.dispatchEvent(
					new PointerEvent("pointerdown", {
						pointerId: e.pointerId,
						button: e.button,
						clientX: e.clientX,
						clientY: e.clientY,
						screenY: e.screenY,
						screenX: e.screenX,
					}),
				);
			});

			// a frame that throws must never stop the game drawing (Pixi's ticker would stop asking for frames): the
			// game's own update is caught here, so the scene is still drawn, and the drawing is guarded by FrameGuard
			pixi.ticker.add(() => {
				try {
					this.render();
				} catch (error) {
					FrameGuard.failed("update", error);
				}
			});
			FrameGuard.protect(pixi);

			const gameContainer = new Container({ isRenderGroup: true });
			gameContainer.addChild(CameraManager.container);
			if (DEBUG_CLIENT) gameContainer.addChild(DebugRenderer.graphics);

			const uiContainer = new Container({ isRenderGroup: true });
			uiContainer.addChild(
				MapManager.container,
				MapManager.mask,
				...Object.values(this.netGraph).map((g) => g.container),
				EmoteWheelManager.container,
				MapPingWheelManager.container,
			);

			pixi.stage.addChild(gameContainer, uiContainer);
			WarLook.attach(gameContainer);

			MapManager.visible = !GameConsole.getBuiltInCVar("cv_minimap_minimized");
			MapManager.expanded = GameConsole.getBuiltInCVar("cv_map_expanded");
			UIManager.ui.gameUi.toggle(GameConsole.getBuiltInCVar("cv_draw_hud"));

			pixi.renderer.on("resize", () => this.resize());
			this.resize();
		};

		let menuMusicSuffix: string;
		if (GameConsole.getBuiltInCVar("cv_use_old_menu_music")) {
			menuMusicSuffix = "_old";
		} else if (this.mode.replaceMenuMusic) {
			const modeName_ = Modes[this.modeName].similarTo ?? this.modeName;
			menuMusicSuffix = `_${modeName_}`;
		} else {
			menuMusicSuffix = "";
		}
		this.music = sound.add("menu_music", {
			url: `./audio/music/menu_music${menuMusicSuffix}.mp3`,
			singleInstance: true,
			preload: true,
			autoPlay: true,
			loop: true,
			volume: GameConsole.getBuiltInCVar("cv_music_volume"),
		});

		void Promise.all([initPixi(), SoundManager.init(), finalizeUI()]).then(
			() => {
				unlockPlayButtons();
				resetPlayButtons();
			},
		);
	}

	resize(): void {
		MapManager.resize();
		CameraManager.resize(true);
		WarLook.resize();
	}

	updateAmbience(): void {
		if (!this.activePlayer || this.mode.noRiverAmbience) return;
		// (0.98.5: frozen in the snow, the river and the sea make no sound; the snow's wind is the sky's)
		if (MapManager.terrain.waterType === FloorNames.Ice) {
			this.riverAmbience.volume = 0;
			this.oceanAmbience.volume = 0;
			return;
		}

		const position = this.activePlayer.position;

		// TODO Optimize this function to use the distance from the edge of the map
		// rather than the beach hitbox
		const oceanDistance = MapManager.distanceToShoreSquared(position);
		this.oceanAmbience.volume = Numeric.delerp(oceanDistance, 4225, 0);

		let riverWeight = 0;
		const rivers = MapManager.terrain.rivers;
		for (let i = 0, len = rivers.length; i < len; i++) {
			// hasanger mwah optimization
			const river = rivers[i];
			if (river.isTrail) continue;

			const closestPoint = river.getPosition(river.getClosestT(position)),
				distanceToRiver = Geometry.distanceSquared(position, closestPoint),
				riverWidth = river.waterWidths[i] + 2,
				normalizedDistance = Numeric.delerp(
					distanceToRiver,
					(30 + riverWidth) ** 2,
					riverWidth ** 2,
				),
				riverStrength = Numeric.clamp(river.waterWidths[i] / 8, 0.25, 1);
			riverWeight = Numeric.max(
				normalizedDistance * riverStrength,
				riverWeight,
			);
		}
		this.riverAmbience.volume = riverWeight;
	}

	connect(address: string, reconnecting = false): void {
		this._leaving = false;
		// (the reactions this match takes from the player: its ticket's)
		try {
			ReactionWheel.useTicket(new URL(address).searchParams.get("ticket"), reconnecting);
		} catch {
			ReactionWheel.useTicket(null, reconnecting);
		}
		// the join packet takes the design this address wears: asked for again now, and the join waits for the answer a
		// moment (1.5 s at most). It used to go with whatever the page had at hand, which, just after another address
		// signed in on this browser, was the previous address's design
		let wornReady: Promise<unknown> = Promise.resolve();
		if (!reconnecting) {
			wornReady = Promise.race([
				refreshWornSkin(true),
				new Promise((resolve) => setTimeout(resolve, 1500)),
			]);
			UIManager.lastKiller = undefined;
			UIManager.lastKill = undefined;
			UIManager.deathCause = undefined;
			UIManager.maxAlive = 0;
			this.error = false;
			this._connectionAddress = address;
			// lag smoothing starts off in every game (the connection may have changed)
			LagSmoothing.reset();
		}

		if (this.gameStarted && !reconnecting) return;

		const socket = new WebSocket(address);
		this._socket = socket;
		this._socket.binaryType = "arraybuffer";

		this._socket.onopen = (): void => {
			this._reconnectUntil = 0;
			this._reconnectAttempts = 0;
			this._reconnectRequestPending = false;
			if (this._reconnectTimer) clearTimeout(this._reconnectTimer);
			this._reconnectTimer = undefined;
			this.pixi.start();
			this.music?.stop();
			this.connecting = false;
			this.gameStarted = true;
			if (this.isTraining) TrainingHud.enter();
			this.gameOver = false;
			this.spectating = false;
			if (!reconnecting) this._matchOver = this._endReached = this._sawWinnerCard = false;
			// a reconnect: the server starts this player's view again from nothing (everything in sight comes with the next
			// update), so the world here does too. What changed while the connection was down (a player gone, loot taken, a
			// crate broken) used to stay on screen, frozen, for the rest of the match
			if (reconnecting) this._resetWorld();

			for (const graph of Object.values(this.netGraph)) graph.clear();

			if (!UI_DEBUG_MODE && !reconnecting) {
				clearTimeout(UIManager.gameOverScreenTimeout);
				const ui = UIManager.ui;

				ui.gameOverOverlay.hide();
				ui.killMsgModal.hide();
				ui.killMsgCounter.text("0");
				ui.killFeed.html("");
				ui.spectatingContainer.hide();
				ui.joystickContainer.show();
			}

			let skin: (typeof defaultClientCVars)["cv_loadout_skin"];
			// (sent on this connection only: a newer one may have taken over while the design was asked for)
			void wornReady.then(() => {
				if (this._socket !== socket) return;
				this.sendPacket(
					JoinPacket.create({
						isMobile: InputManager.isMobile,
						name: GameConsole.getBuiltInCVar("cv_player_name"),
						skin:
							Skins.fromStringSafe(getSavedSkin() ?? "") ??
							Skins.fromStringSafe(
								GameConsole.getBuiltInCVar("cv_loadout_skin"),
							) ??
							Skins.fromString(
								typeof (skin = defaultClientCVars.cv_loadout_skin) === "object"
									? skin.value
									: skin,
							),
						badge: Badges.fromStringSafe(
							GameConsole.getBuiltInCVar("cv_loadout_badge"),
						),
						emotes: EMOTE_SLOTS.map((slot) =>
							Emotes.fromStringSafe(
								GameConsole.getBuiltInCVar(`cv_loadout_${slot}_emote`),
							),
						),
						emblem: getSavedEmblem(),
					}),
				);
			});

			this.gasRender.graphics.zIndex = 1000;
			CameraManager.addObject(this.gasRender.graphics);
			MapManager.indicator.setFrame("player_indicator");

			const particleEffects = this.mode.particleEffects;

			if (particleEffects !== undefined) {
				const This = this;
				const gravityOn = particleEffects.gravity;
				ParticleManager.addEmitter({
					delay: particleEffects.delay,
					active: GameConsole.getBuiltInCVar("cv_ambient_particles"),
					spawnOptions: () => ({
						frames: particleEffects.frames,
						get position(): Vector {
							const width = CameraManager.width / PIXI_SCALE;
							const height = CameraManager.height / PIXI_SCALE;
							const player = This.activePlayer;
							if (!player) return Vec(0, 0);
							const { x, y } = player.position;
							return randomVector(x - width, x + width, y - height, y + height);
						},
						speed: randomVector(-10, 10, gravityOn ? 10 : -10, 10),
						lifetime: randomFloat(12000, 50000),
						zIndex: Number.MAX_SAFE_INTEGER - 5,
						alpha: {
							start: this.layer === Layer.Ground ? 0.7 : 0,
							end: 0,
						},
						rotation: {
							start: randomFloat(0, 36),
							end: randomFloat(40, 80),
						},
						scale: {
							start: randomFloat(0.8, 1.1),
							end: randomFloat(0.7, 0.8),
						},
					}),
				});
			}
		};

		// Handle incoming messages
		this._socket.onmessage = (message: MessageEvent<ArrayBuffer>): void => {
			const stream = new PacketStream(message.data);
			let iterationCount = 0;
			const splits = [0, 0, 0, 0, 0, 0, 0] satisfies DataSplit;
			while (true) {
				if (++iterationCount === 1e3) {
					console.warn(
						"1000 iterations of packet reading; possible infinite loop",
					);
				}
				const packet = stream.deserialize(splits);
				if (packet === undefined) break;
				this.onPacket(packet);
			}
			// (a view's catch-up is one message: over at its end, 1.06.1)
			JumpInView.messageRead();
			this._sayMessageReactions();

			const msgLength = message.data.byteLength;
			this.netGraph.receiving.addEntry(msgLength, splits);
		};

		const ui = UIManager.ui;

		this._socket.onerror = (): void => {
			this.pixi.stop();
			if (reconnecting) return;
			this.error = true;
			this.connecting = false;
			ui.splashMsgText.html(translate("msg_err_joining"));
			ui.splashMsg.show();
			resetPlayButtons();
		};

		this._socket.onclose = (e: CloseEvent): void => {
			this.pixi.stop();
			this.connecting = false;
			this.socketCloseCallback?.(undefined);
			resetPlayButtons();

			const reason = e.reason || "Connection lost";
			// (as the player reads it: the server's words, in the language picked where the phrasebook has them)
			const shownReason = e.reason ? tr(e.reason) : t("Connection lost");

			if (reason.startsWith("Invalid game version")) {
				alert(shownReason);
				// reload the page with a time stamp to try clearing cache
				location.search = `t=${Date.now()}`;
			}

			// we closed it, leaving the game (endGame): nothing was lost, and the reason already shown stays
			if (this._leaving) return;

			// Only a living player whose connection dropped gets it back. Someone watching (from the lobby, or after they
			// died) has no player of theirs to reconnect to, and when the server closed the connection on purpose (the
			// game ended, they were removed) there's nothing to come back to: retrying those for 30 s hid the reason, and
			// hammered the game until it refused every new connection from that address for a minute ("Error joining game")
			const dropped = [1001, 1006, 1011, 1012, 1013, 1014].includes(e.code);
			if (
				!this.gameOver &&
				this.gameStarted &&
				!this.isTraining &&
				!this.spectating &&
				dropped
			) {
				this._scheduleReconnect(reason);
				return;
			}

			// watching, and the end of the match has reached the screen: the match let them go, as it does a moment
			// after the end. Its end card (or the winner's) stays up with its way back to the menu: the menu used to take
			// over a second after the end
			if (this.gameStarted && this._endReached && !this.isTraining) {
				ui.btnSpectate.addClass("btn-disabled");
				RewindOverlay.stopped();
				return;
			}

			// watching: back to the menu, saying why (it used to stay on a frozen view, or try to reconnect as a player)
			if (this.gameStarted && this.spectating && !this.isTraining) {
				ui.splashUi.fadeIn(400);
				ui.splashMsgText.text(
					/game ended/iu.test(reason) || this._matchOver
						? t("The game you were watching has ended.")
						: t("<reason>. Open Spectate to watch again.", { reason: shownReason.replace(/[.。।]$/u, "") }),
				);
				ui.splashMsg.show();
				ui.btnSpectate.addClass("btn-disabled");
				void this.endGame();
				return;
			}

			if (!this.gameOver) {
				if (this.gameStarted) {
					ui.splashUi.fadeIn(400);
					// the server's words, as text (a ban's reason is typed by an admin; nothing in it is markup)
					ui.splashMsgText.text(shownReason);
					ui.splashMsg.show();
				}
				ui.btnSpectate.addClass("btn-disabled");
				if (!this.error) void this.endGame();
			} else {
				for (const sound of SoundManager.updatableSounds) {
					sound.stop();
				}
				// (the rain, the wind and a thunder still rolling in stop with them: 1.06.1's audit)
				WarAudio.hush();
				// on the death screen, and the game has let go of us (the match is over, or the connection is gone):
				// Watch can't show anything any more, so it says so (it used to stay lit and do nothing)
				ui.btnSpectate
					.addClass("btn-disabled")
					.html(
						/game (ended|killed)/iu.test(reason)
							? `<i class="fa-solid fa-eye-slash"></i> ${t("Round over")}`
							: `<i class="fa-solid fa-eye-slash"></i> ${t("Disconnected")}`,
					);
			}
		};
	}

	private _scheduleReconnect(reason: string): void {
		if (this.gameOver || this.isTraining || this._reconnectRequestPending)
			return;
		const now = Date.now();
		this._reconnectUntil ||= now + ROUND_LIMITS.reconnectGraceMs;
		const ui = UIManager.ui;
		ui.splashUi.fadeIn(200);
		ui.splashMsgText.text(t("Connection lost. Reconnecting…"));
		ui.splashMsg.show();

		if (now >= this._reconnectUntil || !this._connectionAddress) {
			this._reconnectUntil = 0;
			ui.splashMsgText.text(
				t("<reason>. The 30-second reconnect window expired.", { reason: tr(reason) }),
			);
			void this.endGame();
			return;
		}

		this._reconnectRequestPending = true;
		// slower each time (half a second, then up to 3 s): a dozen tries in the 30 s, not sixty
		const delay = Math.min(3000, 500 * ++this._reconnectAttempts);
		this._reconnectTimer = setTimeout(() => {
			void requestReconnectAdmission()
				.then((admission) => {
					if (this.gameOver || !this._connectionAddress) return;
					const address = new URL(this._connectionAddress);
					address.searchParams.set("ticket", admission.ticket);
					this._reconnectRequestPending = false;
					this.connect(address.toString(), true);
				})
				.catch((error: unknown) => {
					this._reconnectRequestPending = false;
					// refused (the round is over, or they're out of it): nothing to reconnect to, so say why and stop
					const status = (error as { readonly status?: number } | undefined)?.status;
					if (status !== undefined && status >= 400 && status < 500) {
						this._reconnectUntil = 0;
						const why = error instanceof Error ? error.message : reason;
						ui.splashMsgText.text(
							/not admitted to the active round/iu.test(why) ? t("The round is over.") : tr(why),
						);
						void this.endGame();
						return;
					}
					this._scheduleReconnect(reason);
				});
		}, delay);
	}

	/** reconnect tries since the connection was lost (each waits a little longer) */
	private _reconnectAttempts = 0;
	/** the game is being left (endGame closed the connection itself) */
	private _leaving = false;

	inventoryMsgTimeout: number | undefined;

	/** replaying the moments before death (after the rewind); then spectating, 15 s behind live */
	replaying = false;
	delayedView = false;

	/** the server's rewind markers (see RewindPacket): the death rewind, a delayed watch starting, a quiet reset */
	onRewind(seconds: number, mode: RewindMode): void {
		switch (mode) {
			case RewindMode.Watch:
				// watching from the lobby: 15 s behind live from the start, no rewind; a card until it begins
				this._delaySeconds = seconds;
				this.delayedView = true;
				this._watchWaiting = true;
				document.body.classList.add("delayed-view");
				MapManager.visible = false;
				JumpInView.catchUp();
				RewindOverlay.waiting(seconds);
				// someone watching from the lobby isn't one of the match's players: reports are theirs only
				UIManager.ui.btnReport.hide();
				break;
			case RewindMode.Reset:
				// switching whose view you watch: the new view follows (from a full snapshot), this far behind live
				this._delaySeconds = seconds;
				this._resetWorld();
				JumpInView.catchUp();
				this._badgeFor = undefined;
				break;
			case RewindMode.Caught:
				// the death replay reached the moment of death: from here on it's spectating
				this.endReplay(seconds);
				break;
			case RewindMode.MatchOver:
				// (live) the match has just ended; this view, behind live, still plays its last moments
				this._matchOver = true;
				RewindOverlay.matchOver(seconds, this._delaySeconds);
				break;
			case RewindMode.Ended:
				// the moment the match ended has reached the screen: the end card (unless the winner's card is up)
				this._endReached = true;
				// (the spectating controls, the gas warning and the like go: the match is over)
				document.body.classList.add("match-ended");
				UIManager.ui.spectatingContainer.hide();
				RewindOverlay.ended(!this._sawWinnerCard, seconds, () => void this.endGame());
				break;
			default:
				this.beginRewind(seconds);
		}
	}

	/** told (live) that the match being watched is over: its last moments still play on this screen */
	private _matchOver = false;
	/** the end of the match being watched has reached the screen */
	private _endReached = false;
	/** the end of the match being watched has reached the screen (its card up: no more lightning, 1.06.1) */
	get matchEnded(): boolean {
		return this._endReached;
	}
	/** the winner's card reached this delayed view (it's the end screen, then) */
	private _sawWinnerCard = false;

	private _watchWaiting = false;
	/** our own player (to tell our own last moments from the killer's view in the replay) */
	private _ownId?: number;
	private _replayBadgeFor?: number;

	/** the server rewound the view after a death: clear the world, play the animation, hide the HUD */
	beginRewind(seconds: number): void {
		this.replaying = true;
		this.delayedView = true;
		this._replayBadgeFor = undefined;
		document.body.classList.add("delayed-view", "replaying");
		UIManager.ui.gameOverOverlay.hide();
		MapManager.visible = false;
		ControlsCard.putAway();
		RewindOverlay.play(seconds);
		this._resetWorld();
		JumpInView.catchUp();
		Artillery.reset();
		GasMaskView.reset();
	}

	/** whose view is replaying: the killer's (you see yourself get killed), or your own last moments */
	private _updateReplayBadge(): void {
		if (!this.replaying || this._replayBadgeFor === this.activePlayerID) return;
		this._replayBadgeFor = this.activePlayerID;
		if (this.activePlayerID === this._ownId) {
			RewindOverlay.replayLabel(t("◀◀ REPLAY · YOUR LAST MOMENTS"));
			return;
		}
		const name = (this.playerNames.get(this.activePlayerID)?.name ?? "")
			.replace(/<[^>]*>/gu, "")
			.trim();
		RewindOverlay.replayLabel(
			name ? t("◀◀ REPLAY · <name>'S VIEW", { name }) : t("◀◀ REPLAY · YOUR KILLER'S VIEW"),
		);
	}

	/** clear the world (objects, bullets, effects) without leaving the game */
	private _resetWorld(): void {
		for (const object of this.objects) object.destroy();
		for (const plane of this.planes) plane.destroy();
		this.objects.clear();
		this.planes.clear();
		// (the jump-in's planes, chutes and HUD: the next view's updates bring them back, 1.06; the blood trail's HUD too)
		JumpInView.reset();
		BloodTrailMarks.reset();
		BloodTrailHud.reset();
		this._clearEffects();
	}

	/** short-lived effects that belong to one view (bullets, particles, damage numbers, fire, hit markers) */
	private _clearEffects(): void {
		// (their sprites go too: the layers they're drawn in stay, so a tracer or a spark in flight at a switch of
		// view stayed frozen on the map)
		for (const bullet of [...this.bullets]) bullet.destroy();
		this.bullets.clear();
		ParticleManager.reset();
		GoreManager.reset();
		CombatFx.reset();
		GravityFx.reset();
		OctoFx.reset();
		DamageNumbers.clear();
		FireEffects.reset();
		AtticDark.reset();
		CombatFeedback.reset();
		// (the boot prints in the snow and the splashes: the replay walked into the prints it was yet to make: 1.06.1's audit)
		WarWeather.clearTransient();
	}

	/** the replay reached the moment of death: from here on it's spectating, this many seconds behind live */
	endReplay(seconds = this._delaySeconds): void {
		this._delaySeconds = seconds;
		this.replaying = false;
		document.body.classList.remove("replaying");
		this.spectating = true;
		ReactionWheel.reset();
		UIManager.refreshSpectating();
		this._badgeFor = undefined;
		this._updateSpectateBadge();
	}

	/** how far behind live the view is (seconds), as the server said */
	private _delaySeconds = 15;

	private _badgeFor?: number;

	/** the badge names who you're watching (it changes when they die and you move on to their killer) */
	private _updateSpectateBadge(): void {
		if (
			!this.delayedView ||
			this.replaying ||
			!this.spectating ||
			this._badgeFor === this.activePlayerID
		)
			return;
		this._badgeFor = this.activePlayerID;
		const name = (this.playerNames.get(this.activePlayerID)?.name ?? "")
			.replace(/<[^>]*>/gu, "")
			.trim();
		const seconds = this._delaySeconds;
		RewindOverlay.status(
			name ? t("SPECTATING <name> · <seconds> S BEHIND LIVE", { name, seconds }) : t("SPECTATING · <seconds> S BEHIND LIVE", { seconds }),
		);
	}

	onPacket(packet: PacketDataOut): void {
		// how someone died decides what's left of them (Blood & gore), in the death replay too
		if (packet.type === PacketType.Kill && packet.killed) GoreManager.killInfo(packet);
		// a delayed view is someone else's own view: only the world, the markers and the kill feed count (not their
		// pickups, reports or death card; spectators see only the winner's card). During the death replay, not even
		// the kill feed (the killer's own kill would pop up)
		if (this.delayedView) {
			if (packet.type === PacketType.GameOver) {
				if (this.replaying || packet.rank !== 1) return;
			} else if (
				packet.type !== PacketType.Update &&
				packet.type !== PacketType.Rewind &&
				packet.type !== PacketType.Artillery &&
				!(packet.type === PacketType.Kill && !this.replaying)
			)
				return;
		}
		switch (packet.type) {
			case PacketType.Rewind:
				this.onRewind(packet.seconds, packet.mode);
				break;
			case PacketType.Artillery:
				Artillery.onPacket(packet);
				break;
			case PacketType.Joined:
				this.startGame(packet);
				break;
			case PacketType.Map: {
				// the match's sky (1.05: a clear day, dusk, night, snow or a thunderstorm) before its colours and its map
				const sky = packet.sky ?? "clear";
				WarSky.begin(sky);
				// (0.98.6: the sun and the atmosphere under this sky, and the shadows it throws)
				Lighting.begin(sky);
				ShadowLook.begin();
				// (0.98.4: the hazard the zone is under this sky, the storm's weather and the night's creep)
				HazardRender.begin(sky);
				StormView.begin(sky);
				NightCreep.begin(sky);
				SnowLook.begin(sky);
				WarLook.applyPalette();
				WarSky.applyColors();
				MapManager.updateFromPacket(packet);
				WarLook.setupMap(packet.seed, sky);
				StormView.mapReady();
				SnowLook.mapReady();
				// (the planes are built before the sky is known: in the hazard's colour now)
				this.gasRender.recolor(this.colors.gas);
				MapManager.gasRender?.recolor(this.colors.gas);
				break;
			}
			case PacketType.Update:
				this.processUpdate(packet);
				// remember our own player while alive (the replay may show our own last moments)
				if (!this.delayedView && !this.spectating)
					this._ownId = this.activePlayerID;
				if (this._watchWaiting) {
					this._watchWaiting = false;
					RewindOverlay.hideWaiting();
				}
				this._updateReplayBadge();
				this._updateSpectateBadge();
				break;
			case PacketType.GameOver:
				UIManager.showGameOverScreen(packet);
				// (in a delayed view only the winner's card gets this far: it's the end screen, then)
				if (this.delayedView) {
					this._sawWinnerCard = true;
					RewindOverlay.hideEndCard();
				}
				break;
			case PacketType.Kill:
				UIManager.processKillPacket(packet);
				break;
			case PacketType.Report: {
				UIManager.processReportPacket(packet);
				break;
			}
			case PacketType.Pickup: {
				const { message, item } = packet;

				if (message !== undefined) {
					const { inventoryMsg } = UIManager.ui;
					inventoryMsg
						.text(translate(this._inventoryMessageMap[message]))
						.fadeOut(0)
						.fadeIn(250);

					clearTimeout(this.inventoryMsgTimeout);
					this.inventoryMsgTimeout = window.setTimeout(
						() => inventoryMsg.fadeOut(250),
						2500,
					);
				} else if (item !== undefined) {
					let soundID: string;
					switch (item.defType) {
						case DefinitionType.Ammo:
							soundID = "ammo_pickup";
							break;
						case DefinitionType.HealingItem:
							soundID = `${item.idString}_pickup`;
							break;
						case DefinitionType.Scope:
							soundID = "scope_pickup";
							break;
						case DefinitionType.Armor:
							if (item.armorType === ArmorType.Helmet)
								soundID = "helmet_pickup";
							else soundID = "vest_pickup";
							break;
						case DefinitionType.Backpack:
							soundID = "backpack_pickup";
							break;
						case DefinitionType.Throwable:
							soundID = "throwable_switch";
							break;
						case DefinitionType.Perk:
							soundID = "pickup";
							break;
						default:
							soundID = "pickup";
							break;
					}

					SoundManager.play(soundID);

					if (item.defType === DefinitionType.Skin) {
						// collectable: tell the player it went into their collection
						const { inventoryMsg } = UIManager.ui;
						inventoryMsg
							.text(
								t("Skin collected: <skin> (Load it in the emblem editor)", { skin: item.name }),
							)
							.fadeOut(0)
							.fadeIn(250);
						clearTimeout(this.inventoryMsgTimeout);
						this.inventoryMsgTimeout = window.setTimeout(
							() => inventoryMsg.fadeOut(250),
							4000,
						);
						noteSkinCollected(item.idString);
					}
				} else {
					console.warn("Unexpected PickupPacket with neither message nor item");
				}
				break;
			}
		}
	}

	private readonly _inventoryMessageMap: Record<
		InventoryMessages,
		TranslationKeys
	> = {
		[InventoryMessages.NotEnoughSpace]: "msg_not_enough_space",
		[InventoryMessages.ItemAlreadyEquipped]: "msg_item_already_equipped",
		[InventoryMessages.BetterItemEquipped]: "msg_better_item_equipped",
		[InventoryMessages.CannotUseFlare]: "msg_cannot_use_flare",
		[InventoryMessages.BuildNeedWood]: "msg_build_need_wood",
		[InventoryMessages.BuildBlockedTree]: "msg_build_blocked_tree",
		[InventoryMessages.BuildBlockedRock]: "msg_build_blocked_rock",
		[InventoryMessages.BuildBlockedCrate]: "msg_build_blocked_crate",
		[InventoryMessages.BuildBlockedWall]: "msg_build_blocked_wall",
		[InventoryMessages.BuildBlockedMetal]: "msg_build_blocked_metal",
		[InventoryMessages.BuildBlockedPlayer]: "msg_build_blocked_player",
		[InventoryMessages.BuildBlockedSomething]: "msg_build_blocked_something",
		[InventoryMessages.BuildBlockedEdge]: "msg_build_blocked_edge",
	};

	startGame(packet: JoinedData): void {
		// Sound which notifies the player that the
		// game started if page is out of focus.
		if (!document.hasFocus()) SoundManager.play("join_notification");
		// the controls card on your first match (afterwards a Controls chip under the players counter)
		ControlsCard.matchStarted();
		// (the menu's store closed: its keys never reach the match; the reactions' wheel starts closed; the catalogue read
		// again, so the pictures and clips the match shows are the store's files now)
		StoreMenu.close(false);
		ReactionWheel.reset();
		void StoreCatalog.load();

		// (a reconnect starts the game again: the loops already playing stop first. Each reconnect used to add three
		// more, the old river and sea kept the volume they had at the drop, and the wind played twice)
		this.ambience?.stop();
		this.riverAmbience?.stop();
		this.oceanAmbience?.stop();
		const ambience = this.mode.ambience;
		if (ambience) {
			this.ambience = SoundManager.play(ambience, {
				loop: true,
				ambient: true,
			});
			if (this.mode.ambienceVolume !== undefined) {
				this.ambience.volume = this.mode.ambienceVolume;
			}
		}

		if (!this.mode.noRiverAmbience) {
			this.riverAmbience = SoundManager.play("river_ambience", {
				loop: true,
				ambient: true,
			});
			this.riverAmbience.volume = 0;

			this.oceanAmbience = SoundManager.play("ocean_ambience", {
				loop: true,
				ambient: true,
			});
			this.oceanAmbience.volume = 0;
		}

		const emotes = (EmoteWheelManager.emotes = packet.emotes
			.slice(0, 6)
			.filter((e) => e !== undefined)
			.map(({ idString }) => idString));
		const len = emotes.length;
		emotes.push(...emotes.splice(0, ((1 % len) + len) % len)); // rotates the array so emotes appear in the correct order
		EmoteWheelManager.setupSlots();
		UIManager.updateRequestableItems();
		MapManager.resize();
		// the map as the settings have it: a death replay or a watch from the lobby hid it (the next match had none), and
		// the big map open as the last one ended starts shut (1.06.1's audit); none in the Training Range, whose HUD has
		// hidden it as the connection opened (1.06.1's second review: shown again here)
		if (GameConsole.getBuiltInCVar("cv_map_expanded")) GameConsole.setBuiltInCVar("cv_map_expanded", false);
		MapManager.expanded = false;
		MapManager.visible = !TrainingHud.active && !GameConsole.getBuiltInCVar("cv_minimap_minimized") && GameConsole.getBuiltInCVar("cv_draw_hud");

		const ui = UIManager.ui;

		if ((this.isTeamMode = packet.teamMode !== TeamMode.Solo)) {
			this.teamID = packet.teamID;
		}

		ui.inventoryMsg.fadeOut(PERK_MESSAGE_FADE_TIME);

		ui.canvas.addClass("active");
		ui.splashUi.fadeOut(400, () => resetPlayButtons());

		ui.killLeaderLeader.html(translate("msg_waiting_for_leader"));
		ui.killLeaderCount.text("0");
		ui.spectateKillLeader.addClass("btn-disabled");

		if (!UI_DEBUG_MODE) ui.teamContainer.toggle(this.isTeamMode);

		// send the packet to sync our local configs when joining
		this.debugMenu?.sendPacket();
	}

	async endGame(): Promise<void> {
		this._reconnectUntil = 0;
		this._reconnectRequestPending = false;
		if (this._reconnectTimer) clearTimeout(this._reconnectTimer);
		this._reconnectTimer = undefined;
		Artillery.reset();
		GasMaskView.reset();
		this.replaying = false;
		this.delayedView = false;
		this._watchWaiting = false;
		this._matchOver = this._endReached = this._sawWinnerCard = false;
		this._ownId = undefined;
		// (hidden while watching from the lobby)
		UIManager.ui.btnReport.show();
		document.body.classList.remove("delayed-view", "replaying", "match-ended");
		RewindOverlay.hide();
		TrainingHud.leave();
		this.isTraining = false;
		LagSmoothing.reset();
		DamageNumbers.clear();
		CombatFeedback.reset();
		WarLook.reset();
		FireEffects.reset();
		ActionHints.reset();
		InventoryScreen.reset();
		ReactionWheel.reset();
		ReactionWheel.forgetMatch();
		clearAutoPickupHolds();
		return await new Promise((resolve) => {
			const ui = UIManager.ui;

			ui.gameMenu.fadeOut(250);
			ui.splashOptions.addClass("loading");
			ui.loaderText.text("");

			SoundManager.stopAll();
			// (no lightning in the menu's fade: its thunder rolled on over the menu, 1.06.1's second review)
			WarSky.quiet();
			this.gameOver = true;
			// our own close: its close event must not be taken for a lost connection (it replaced the reason shown)
			this._leaving = true;
			this._socket?.close();

			ui.splashUi.fadeIn(400, async () => {
				this.pixi.stop();
				// (the sky and the attic's dark once nothing more is drawn: reset before the menu's fade, the night's dark
				// and the dusk went off the map behind it, and the last frame left was a daylit one: 1.06.1's audit)
				WarSky.reset();
				Lighting.reset();
				ShadowLook.reset();
				NormalLook.reset();
				LightFans.reset();
				DamageMarks.reset();
				AtticDark.reset();
				HazardRender.reset();
				StormView.reset();
				NightCreep.reset();
				SnowLook.reset();
				void this.music?.play();

				for (const object of this.objects) object.destroy();
				for (const plane of this.planes) plane.destroy();
				JumpInView.reset();
				BloodTrailMarks.reset();
				BloodTrailHud.reset();
				this.objects.clear();
				this.bullets.clear();
				this.planes.clear();
				this.playerNames.clear();
				this._timeouts.clear();

				GoreManager.reset();
				CombatFx.reset();
				GravityFx.reset();
				CameraManager.reset();
				GasManager.reset();
				MapManager.reset();
				ParticleManager.reset();
				ScreenRecordManager.reset();
				UIManager.reset();

				// Wait for the socket to close if it hasn't already
				if (this._socket?.readyState !== WebSocket.CLOSED) {
					await new Promise((resolve) => (this.socketCloseCallback = resolve));
				}

				updateDisconnectTime();
				resetPlayButtons();

				this.gameStarted = false;

				if (teamSocket) ui.createTeamMenu.fadeIn(250, resolve);
				else resolve();
			});
		});
	}

	private readonly _packetStream = new PacketStream(new ArrayBuffer(8192)); // join packets carry the emblem
	/** whether the match's connection is open (a packet sent otherwise goes nowhere) */
	get connected(): boolean {
		return this._socket?.readyState === WebSocket.OPEN;
	}

	sendPacket(packet: PacketDataIn): void {
		this._packetStream.stream.index = 0;
		this._packetStream.serialize(packet);
		this.sendData(this._packetStream.getBuffer());
	}

	sendData(buffer: ArrayBuffer): void {
		if (this._socket?.readyState === WebSocket.OPEN) {
			this.netGraph.sending.addEntry(buffer.byteLength);
			try {
				this._socket.send(buffer);
			} catch (e) {
				console.warn("Error sending packet. Details:", e);
			}
		}
	}

	render(): void {
		if (!this.gameStarted) return;
		Artillery.update();
		GasMaskView.update();
		const delta = this.pixi.ticker.deltaMS;
		DamageNumbers.update(delta);
		CombatFeedback.update(delta, this.activePlayer?.position);

		// execute timeouts
		const now = Date.now();
		for (const timeout of this._timeouts) {
			if (timeout.killed) {
				this._timeouts.delete(timeout);
				continue;
			}
			if (now > timeout.end) {
				timeout.callback();
				this._timeouts.delete(timeout);
			}
		}

		const hasMovementSmoothing = GameConsole.getBuiltInCVar(
			"cv_movement_smoothing",
		);

		const showHitboxes = GameConsole.getBuiltInCVar("db_show_hitboxes");

		for (const object of this.objects) {
			object.update();
			if (hasMovementSmoothing) object.updateInterpolation();

			if (DEBUG_CLIENT) {
				if (showHitboxes) object.updateDebugGraphics();
			}
		}

		if (hasMovementSmoothing && this.activePlayer) {
			CameraManager.position = this.activePlayer.container.position;
		}

		for (const [image, spinSpeed] of this.spinningImages.entries()) {
			image.rotation += spinSpeed * delta;
		}

		for (const tween of this.tweens) tween.update();

		// (0.98.8: what they could strike, gathered once for the frame: objects/bulletTargets.ts)
		if (this.bullets.size > 0) BulletTargets.gather(this.objects);
		for (const bullet of this.bullets) bullet.update(delta);

		ParticleManager.update(delta);
		GoreManager.update(delta);
		// the blood trails' marks drying, and their HUD (1.06)
		BloodTrailMarks.update();
		BloodTrailHud.tick();
		// the first match's controls card, once you're down on the ground (1.06: a match starts in a plane)
		ControlsCard.update();
		CombatFx.update(delta);
		GravityFx.update(delta);
		// the Octo Gadget's arms (1.10): alive on the backs that carry it
		OctoFx.update(delta);

		MapManager.update();
		this.gasRender.update();
		// the hazard's edge along the zone (0.98.4: the dust storm's wall, the wildfire's line) and the storm's weather
		HazardRender.update();
		StormView.update(delta);
		NightCreep.update(delta);
		// (0.98.5: the snow's caps baked a few a frame)
		SnowLook.update();

		for (const plane of this.planes) plane.update();
		// the jump-in (1.06): the planes, the chutes' camera, the ring and the HUD (before the camera: it may move it)
		JumpInView.update();

		// (0.98.8: a click looks for a player under it only while spectating, the only time a click on one does
		// anything, objects/player.ts; playing, the world's thousands of things aren't walked through for every click)
		CameraManager.container.eventMode = this.spectating ? "passive" : "none";
		CameraManager.update();
		// (the jump-in's sky, over the world's container: moved with the camera)
		JumpInView.afterCamera();
		// (0.98.6: the sun this frame, the clouds' shadows and the glows; then the shadows the sun throws, drawn into
		// their picture before the frame; the shaders take the sun after)
		Lighting.update(delta);
		ShadowLook.update();
		// (0.98.8: the frame's clock and the ground's normal pass's camera, before the shapes' picture draws that pass)
		WarLook.prepare(delta);
		// (0.98.7: the picture of the shapes on screen, then the lights' frame (the walls that cut them, the relief's
		// picture begun), then the day's glows as fans; the night's lights come after the attic's dark, and the grade
		// takes the relief once the last light is drawn)
		NormalLook.update();
		LightFans.update();
		Lighting.drawGlows();
		WarLook.update(delta);
		WarSky.update();
		FireEffects.update();
		AtticDark.update();
		// (the night's dark after the attic's: it's cut away where the attic's shows)
		NightLight.update();
		LightFans.finish();
		WarLook.finishLighting();
		ActionHints.update();
		TrainingHud.update();

		EmoteWheelManager.update();
		MapPingWheelManager.update();

		if (DEBUG_CLIENT) {
			DebugRenderer.graphics.position = CameraManager.container.position;
			DebugRenderer.graphics.scale = CameraManager.container.scale;
			DebugRenderer.render();
		}

		if (GameConsole.getBuiltInCVar("pf_show_fps")) {
			const fps = Math.round(this.pixi.ticker.FPS);
			this.netGraph.fps.addEntry(fps);
		}
	}

	private _lastUpdateTime = 0;
	get lastUpdateTime(): number {
		return this._lastUpdateTime;
	}

	/**
	 * Otherwise known as "time since last update", in milliseconds
	 */
	private _serverDt = 0;
	/**
	 * Otherwise known as "time since last update", in milliseconds
	 */
	get serverDt(): number {
		return this._serverDt;
	}

	private _pingSeq = -1;

	private readonly _seqsSent: Array<number | undefined> = [];
	get seqsSent(): Array<number | undefined> {
		return this._seqsSent;
	}

	takePingSeq(): number {
		const n = (this._pingSeq = (this._pingSeq + 1) % 128);
		this._seqsSent[n] = Date.now();
		return n;
	}

	/** the voice reactions of each update read in the message being read (processUpdate), in their order */
	private _messageReactions: NonNullable<UpdateDataOut["reactions"]>[] = [];

	/**
	 * a message's voice reactions, heard and read over the players, unless reactions are hidden in the settings: its last
	 * few updates' only (MESSAGE_REACTION_UPDATES). A view caught up from a snapshot (watching starts or switches, a death
	 * replay starts) comes as seconds of updates in one message, and every reaction in them started at the same moment
	 * (0.94 audit): those are seconds old already. A tick the server ran late sends two or three in one message, and
	 * their reactions are now (its review: its last update's only lost one reaction in thirty a watcher heard)
	 */
	private _sayMessageReactions(): void {
		const updates = this._messageReactions;
		this._messageReactions = [];
		for (const reactions of updates.slice(-MESSAGE_REACTION_UPDATES)) {
			for (const { playerID, reaction } of reactions) {
				if (GameConsole.getBuiltInCVar("cv_hide_emotes") && playerID !== this.activePlayerID) continue;
				const player = this.objects.get(playerID);
				if (player?.isPlayer) player.showReaction(reaction);
			}
		}
	}

	/** the updates read so far (0.98.8: what's worked out from the objects once an update, not every frame, keeps by it) */
	private _updates = 0;
	get updates(): number {
		return this._updates;
	}

	processUpdate(updateData: UpdateDataOut): void {
		const now = Date.now();
		this._serverDt = now - this._lastUpdateTime;
		this._lastUpdateTime = now;
		this._updates++;
		// before the objects take their new positions: lag smoothing eases them in over the connection's own rhythm
		LagSmoothing.noteUpdate(now);

		for (const {
			id,
			name,
			hasColor,
			nameColor,
			badge,
		} of updateData.newPlayers ?? []) {
			this.playerNames.set(id, {
				name: name,
				hasColor: hasColor,
				nameColor: new Color(nameColor),
				badge: badge,
			});
		}

		const playerData = updateData.playerData;
		if (playerData) {
			UIManager.updateUI(playerData);
			// the blood trail you leave (1.06)
			if (playerData.bloodTrail) BloodTrailHud.update(playerData.bloodTrail);
			// where the server's chute takes yours in its final approach (1.06.1): the ring there
			if (playerData.landing) JumpInView.landingSpot(playerData.landing.spot);
			UIManager.updateWeaponSlots(); // to load reskins

			if (
				this.spectating &&
				playerData.teamID !== undefined &&
				playerData.id !== undefined
			) {
				this.teamID = playerData.teamID;
			}
		}

		for (const deletedPlayerId of updateData.deletedPlayers ?? []) {
			this.playerNames.delete(deletedPlayerId);
		}

		for (const { id, type, data } of updateData.fullDirtyObjects ?? []) {
			const object: GameObject | undefined = this.objects.get(id);

			if (object === undefined || object.destroyed) {
				type K = typeof type;

				const _object = new (
					ObjectClassMapping[type] as new (
						id: number,
						data: ObjectsNetData[K],
					) => InstanceType<ObjectClassMapping[K]>
				)(id, data);
				this.objects.add(_object);
			} else {
				object.updateFromData(data, false);
			}
		}

		for (const { id, data } of updateData.partialDirtyObjects ?? []) {
			const object = this.objects.get(id);
			if (object === undefined) {
				console.warn(
					`Trying to partially update non-existant object with ID ${id}`,
				);
				continue;
			}

			(object as GameObject).updateFromData(data, false);
		}

		for (const id of updateData.deletedObjects ?? []) {
			const object = this.objects.get(id);
			if (object === undefined) {
				console.warn(`Trying to delete unknown object with ID ${id}`);
				continue;
			}

			object.destroy();
			this.objects.delete(object);
		}

		for (const bullet of updateData.deserializedBullets ?? []) {
			this.bullets.add(new Bullet(bullet));
		}

		for (const explosionData of updateData.explosions ?? []) {
			explosion(
				explosionData.definition,
				explosionData.position,
				explosionData.layer,
			);
		}

		for (const emote of updateData.emotes ?? []) {
			// (the emotes hidden: only those; healing item and weapon emotes are always shown, those after one too: the loop
			// used to stop at the first emote hidden, 0.94 audit)
			if (
				GameConsole.getBuiltInCVar("cv_hide_emotes") &&
				emote.definition.defType === DefinitionType.Emote // healing item/weapon emotes are always displayed
			)
				continue;

			const player = this.objects.get(emote.playerID);
			if (player?.isPlayer) {
				player.showEmote(emote.definition);
			} else {
				console.warn(
					`Tried to emote on behalf of ${player === undefined ? "a non-existant player" : `a/an ${ObjectCategory[player.type]}`}`,
				);
				continue;
			}
		}

		// the store's voice reactions (0.92): said once the message is read, its last few updates' only (see onmessage)
		this._messageReactions.push(updateData.reactions ?? []);

		GasManager.updateFrom(updateData);

		// players watching the match (one total, sent only when it changes)
		if (updateData.spectatorCount !== undefined) {
			UIManager.ui.spectatorCount.text(updateData.spectatorCount);
			UIManager.ui.spectatorCounter.toggle(updateData.spectatorCount > 0);
		}
		if (updateData.aliveCount !== undefined) {
			UIManager.maxAlive = Math.max(UIManager.maxAlive, updateData.aliveCount);
			const { playerAlive, btnSpectate } = UIManager.ui;
			playerAlive.text(updateData.aliveCount);
			// (on the death screen it stays: Watch shows the death from the killer's view, and the end with it,
			// even when that death ended the match)
			btnSpectate.toggle(updateData.aliveCount > 1 || UIManager.onDeathScreen);
		}

		for (const plane of updateData.planes ?? []) {
			this.planes.add(new Plane(plane.position, plane.direction));
		}

		// the jump-in's planes (1.06): their clock and ways, and who is aboard (a catch-up's updates read at once move
		// their clock on: 1.06.1)
		JumpInView.noteUpdate(updateData.jumpIn !== undefined);
		if (updateData.jumpIn) JumpInView.updateFrom(updateData.jumpIn);
		// the thunderstorm (0.98.4): the tornadoes crossing and the flood's spread
		if (updateData.storm) StormView.updateFrom(updateData.storm);

		for (const ping of updateData.mapPings ?? []) {
			MapManager.addMapPing(ping);
		}

		for (const indicator of updateData.mapIndicators ?? []) {
			MapManager.updateMapIndicator(indicator);
		}

		if (updateData.killLeader) {
			UIManager.updateKillLeader(updateData.killLeader);
		}

		if (updateData.damageNumbers?.length) {
			const numbers = updateData.damageNumbers;
			CombatFeedback.hit(
				numbers.some((hit) => hit.kill),
				// what was hit wore armour that shows: the marker turns steel grey, the tick a metal tink
				numbers.some((hit) => {
					const target = this.objects.get(hit.targetId);
					return target?.isPlayer === true && (target as Player).armorShows;
				}),
			);
			if (this.isTraining)
				for (const hit of updateData.damageNumbers)
					TrainingHud.onHit(
						hit.amount,
						this.objects.get(hit.targetId)?.position,
					);
			if (GameConsole.getBuiltInCVar("cv_damage_numbers")) {
				for (const hit of updateData.damageNumbers) {
					const target = this.objects.get(hit.targetId);
					if (target)
						DamageNumbers.spawn(
							target.position,
							hit.amount,
							hit.kill,
							hit.targetId,
						);
				}
			}
		}
		if (updateData.damageSources?.length && !this.spectating && !this.gameOver)
			CombatFeedback.damagedFrom(updateData.damageSources);

		this.tick();
	}

	addTween<T extends object>(config: TweenOptions<T>): Tween<T> {
		const tween = new Tween(config);
		this.tweens.add(tween);
		return tween;
	}

	removeTween(tween: Tween<object>): void {
		this.tweens.delete(tween);
	}

	backgroundTween?: Tween<{
		readonly r: number;
		readonly g: number;
		readonly b: number;
	}>;
	volumeTween?: Tween<GameSound>;

	updateLayer(newLayer: Layer, initial = false, oldLayer?: Layer): void {
		CameraManager.updateLayer(newLayer, initial, oldLayer);

		for (const sound of SoundManager.updatableSounds) {
			sound.updateLayer();
			sound.update();
		}
		// slight hack
		// if we don't do this then ambience will play at the wrong volume for a tick
		this.riverAmbience?.layerVolumeTween?.kill();
		this.oceanAmbience?.layerVolumeTween?.kill();
		this.updateAmbience();

		const basement = newLayer === Layer.Basement;
		MapManager.terrainGraphics.visible = !basement;

		const currentColor = this.pixi.renderer.background.color;
		const targetColor = basement ? this.colors.void : this.colors.background;

		if (currentColor.toNumber() !== targetColor.toNumber()) {
			const { red, green, blue } = currentColor;
			const color = { r: red * 255, g: green * 255, b: blue * 255 };

			this.backgroundTween?.kill();
			this.backgroundTween = this.addTween({
				target: color,
				to: {
					r: targetColor.red * 255,
					g: targetColor.green * 255,
					b: targetColor.blue * 255,
				},
				onUpdate: () => {
					this.pixi.renderer.background.color = new Color(color);
				},
				duration: LAYER_TRANSITION_DELAY,
				onComplete: () => {
					this.backgroundTween = undefined;
				},
			});
		}
	}

	// yes this might seem evil. but the two local variables really only need to
	// exist so this method can use them: therefore, making them attributes on the
	// enclosing instance is pointless and might induce people into thinking they
	// can use them to do something when they probably can't and shouldn't
	readonly tick = (() => {
		/**
		 * Context: rerendering ui elements needlessly is bad, so we
		 * determine the information that should trigger a re-render if
		 * changed, and cache them in order to detect such changes
		 *
		 * In the case of the pickup message thingy, those informations are:
		 * - the item the pickup message concerns
		 * - its quantity
		 * - the bind to interact has changed
		 * - whether the user can interact with it
		 */
		const cache: {
			object?: Loot | Obstacle | Player;
			offset?: number;
			isAction?: boolean;
			bind?: string;
			canInteract?: boolean;
			comparisonKey?: string;
			spare?: boolean;
		} = {};

		/**
		 * When a bind is changed, the corresponding html won't
		 * get changed because rendering only occurs when an item
		 * is interactable. We thus store whether the intent to
		 * change was acknowledged here.
		 */
		let bindChangeAcknowledged = false;

		// same idea as above
		const funnyDetonateButtonCache: {
			bind?: string;
		} = {};

		// keep image thingy around to consult (and therefore lazily change) src
		let detonateBindIcon: JQuery<HTMLImageElement> | undefined;

		return () => {
			if (!this.gameStarted || (this.gameOver && !this.spectating)) return;

			InputManager.update();
			SoundManager.update();
			ScreenRecordManager.update();

			const player = this.activePlayer;
			if (!player) return;

			const isAction = UIManager.action.active;
			const showCancel = isAction && !UIManager.action.fake;

			if (isAction) {
				UIManager.updateAction();
			}

			interface CloseObject {
				object?: Loot | Obstacle | Player;
				dist: number;
			}

			const interactable: CloseObject = {
				object: undefined,
				dist: Number.MAX_VALUE,
			};
			const uninteractable: CloseObject = {
				object: undefined,
				dist: Number.MAX_VALUE,
			};
			const detectionHitbox = new CircleHitbox(
				3 * player.sizeMod,
				player.position,
			);

			let hideSecondFloor = false;
			this.onDeck = !player.air && deckAt(player.layer, player.position) !== undefined;

			for (const object of this.objects) {
				const { isLoot, isObstacle, isPlayer, isBuilding } = object;
				// (0.98.8: a loot item's own checks, its bag's room and the guns it'd swap, only once it's within reach:
				// they ran for every item the page knows at every update. The rest as before, its checks then its reach)
				const isInteractable =
					(isLoot || isObstacle || isPlayer) &&
					(!isLoot || (!player.air && object.hitbox.collidesWith(detectionHitbox) && adjacentOrEquivLayer(object, player.layer))) &&
					object.canInteract(player);
				if (
					(isLoot || isInteractable) &&
					// (nothing is picked up from the air, 1.06)
					!player.air &&
					object.hitbox.collidesWith(detectionHitbox) &&
					adjacentOrEquivLayer(object, player.layer)
				) {
					const dist = Geometry.distanceSquared(
						object.position,
						player.position,
					);
					if (isInteractable) {
						// a spare counts as a little farther away, as on the server (common/src/utils/spareGear.ts)
						const reach = pickupReach(
							dist,
							(isLoot && object.isSparePickup(player)) === true,
						);
						if (reach < interactable.dist) {
							interactable.dist = reach;
							interactable.object = object;
						}
					} else if (isLoot && dist < uninteractable.dist) {
						uninteractable.dist = dist;
						uninteractable.object = object;
					}
				} else if (isBuilding) {
					object.toggleCeiling();
					if (
						object.ceilingHitbox !== undefined &&
						!object.ceilingVisible &&
						object.definition.hasSecondFloor
					) {
						hideSecondFloor = true;
					}

					// tree leaves
				} else if (
					isObstacle &&
					object.definition.isTree &&
					object.leavesSprite &&
					!object.dead
				) {
					const {
						minDist = 32,
						maxDist = 729,
						trunkMinAlpha = 0.8,
						leavesMinAlpha = 0.35,
					} = object.definition.tree ?? {};
					// (from the air the trees stay whole: you're over them, not under them, 1.06)
					const dist = player.air
						? maxDist
						: Geometry.distanceSquared(object.position, player.position);
					object.image.alpha = Numeric.remap(
						dist,
						minDist,
						maxDist,
						trunkMinAlpha,
						1,
					);
					object.leavesSprite.alpha = Numeric.remap(
						dist,
						minDist,
						maxDist,
						leavesMinAlpha,
						1,
					);

					// metal detectors
				} else if (
					isObstacle &&
					object.definition.detector &&
					object.notOnCoolDown
				) {
					for (const player of this.objects.getCategory(
						ObjectCategory.Player,
					)) {
						if (
							!object.hitbox.collidesWith(player.hitbox) ||
							!equalLayer(object.layer, player.layer) ||
							player.dead ||
							// (nobody in a plane or under a chute walks through it: 1.06's audit)
							player.air
						)
							continue;

						SoundManager.play("detection", {
							falloff: 0.25,
							position: Vec(object.position.x + 20, object.position.y - 20),
							maxRange: 200,
						});

						object.notOnCoolDown = false;
						setTimeout(() => (object.notOnCoolDown = true), 1000);
					}

					// bush particles
				} else if (
					isObstacle &&
					object.definition.material === "bush" &&
					object.definition.noCollisions
				) {
					for (const player of this.objects.getCategory(
						ObjectCategory.Player,
					)) {
						// (nobody in a plane or under a chute is in a bush: 1.06's audit)
						const inBush =
							player.air === undefined &&
							equalLayer(object.layer, player.layer) &&
							object.hitbox.isPointInside(player.position);

						if (
							(player.bushID === undefined && !inBush) || // not in this bush
							(player.bushID !== undefined && player.bushID !== object.id) || // in a different bush
							player.dead
						)
							continue;

						if (object.dead) {
							player.bushID = undefined;
							continue;
						}

						let bushSound: string | undefined;
						if (player.bushID === undefined) {
							// bush
							player.bushID = object.id;
							bushSound = "bush_rustle_1";
						} else if (!inBush) {
							// in this case we exit bushh lol
							player.bushID = undefined;
							bushSound = "bush_rustle_2";
						}
						if (!bushSound) continue;

						let particle =
							object.definition.frames?.particle ??
							`${object.definition.idString}_particle`;
						if (object.definition.particleVariations) {
							particle += `_${random(1, object.definition.particleVariations)}`;
						}

						ParticleManager.spawnParticles(2, () => ({
							frames: particle,
							position: object.hitbox.randomPoint(),
							zIndex: Numeric.max(
								(object.definition.zIndex ?? ZIndexes.Players) + 1,
								4,
							),
							lifetime: 500,
							scale: {
								start: randomFloat(0.85, 0.95),
								end: 0,
								ease: EaseFunctions.quarticIn,
							},
							alpha: {
								start: 1,
								end: 0,
								ease: EaseFunctions.sexticIn,
							},
							rotation: { start: randomRotation(), end: randomRotation() },
							speed: Vec.fromPolar(randomRotation(), randomFloat(6, 9)),
						}));

						object.playSound(bushSound, {
							falloff: 0.25,
							maxRange: 200,
						});
					}
				}
			}

			this.updateAmbience();

			if (this.hideSecondFloor !== hideSecondFloor) {
				this.hideSecondFloor = hideSecondFloor;
				CameraManager.updateLayer(this.layer);
			}

			const object = interactable.object ?? uninteractable.object;
			const offset = object?.isObstacle ? object.door?.offset : undefined;
			const canInteract = interactable.object !== undefined;
			const isSpare =
				canInteract && object?.isLoot === true && object.isSparePickup(player);

			const bind: string | undefined =
				InputManager.binds.getInputsBoundToAction(
					object === undefined ? "cancel_action" : "interact",
				)[0];
			const lootDefinition = object?.isLoot ? object.definition : undefined;
			// (a gun picked up with no gun in hand and both gun slots full takes the first slot's place: compared with
			// that one, 0.97)
			const swapSlot =
				lootDefinition?.defType === DefinitionType.Gun
					? gunSwapSlot(
							UIManager.inventory.weapons,
							UIManager.inventory.activeWeaponIndex,
							(slot) => (UIManager.inventory.lockedSlots & (1 << slot)) !== 0,
							lootDefinition,
						)
					: -1;
			const activeInventoryWeapon =
				UIManager.inventory.weapons[
					swapSlot === -1 ? UIManager.inventory.activeWeaponIndex : swapSlot
				]?.definition;
			const comparisonPayload = getWeaponComparisonPayload(
				lootDefinition,
				activeInventoryWeapon,
				GameConsole.getBuiltInCVar("cv_weapon_compare"),
				GameConsole.getBuiltInCVar("cv_weapon_compare_condensed"),
			);
			const comparisonKey = comparisonPayload
				? `${comparisonPayload.mode}:${comparisonPayload.key}`
				: undefined;

			const differences = {
				object: cache.object?.id !== object?.id,
				offset: cache.offset !== offset,
				isAction: cache.isAction !== isAction,
				bind: cache.bind !== bind,
				canInteract: cache.canInteract !== canInteract,
				comparison: cache.comparisonKey !== comparisonKey,
				spare: cache.spare !== isSpare,
			};

			if (differences.bind) bindChangeAcknowledged = false;

			if (
				differences.object ||
				differences.offset ||
				differences.isAction ||
				differences.bind ||
				differences.canInteract ||
				differences.comparison ||
				differences.spare
			) {
				// Cache miss, rerender
				cache.object = object;
				cache.offset = offset;
				cache.isAction = isAction;
				cache.bind = bind;
				cache.canInteract = canInteract;
				cache.comparisonKey = comparisonKey;
				cache.spare = isSpare;

				const { interactKey, interactMsg, interactText } = UIManager.ui;
				const type = lootDefinition?.defType;

				// Update interact message
				if (object !== undefined || (isAction && showCancel)) {
					// If the loot object hasn't changed, we don't need to redo the text
					if (
						differences.object ||
						differences.offset ||
						differences.isAction ||
						differences.spare
					) {
						let text;
						switch (true) {
							case object?.isObstacle: {
								if (
									object.definition.isActivatable ||
									object.definition.customInteractMessage
								) {
									text = translate(
										`interact_${object.definition.interactObstacleIdString ?? object.definition.idString}` as TranslationKeys,
									);
								} else if (object.definition.isDoor) {
									text =
										object.door?.offset === 0
											? translate("action_open_door")
											: translate("action_close_door");
								}
								break;
							}
							case object?.isLoot: {
								const definition = object.definition;
								const itemName =
									definition.defType === DefinitionType.Gun && definition.isDual
										? translate("dual_template", {
												gun: translate(
													definition.singleVariant as TranslationKeys,
												),
											})
										: translate(
												("translationString" in definition &&
												"lootAndKillfeedTranslationString" in definition
													? definition.translationString
													: definition.idString) as TranslationKeys,
											);

								// (a Training Range rack's stack never runs out: no count)
								text = `${itemName}${object.count > 1 && Number.isFinite(object.count) ? ` (${object.count})` : ""}`;
								// taken as a spare: kept in the bag, worn only once the piece worn is dropped
								if (isSpare) text = translate("loot_spare", { item: text });
								break;
							}
							case object?.isPlayer: {
								text = translate("action_revive", {
									player: UIManager.getRawPlayerName(object.id),
								});
								break;
							}
							case isAction: {
								text = translate("action_cancel");
								break;
							}
						}

						if (text) interactText.text(text);
					}

					if (
						!InputManager.isMobile &&
						(!bindChangeAcknowledged || (object === undefined && isAction))
					) {
						bindChangeAcknowledged = true;

						const icon =
							bind === undefined
								? undefined
								: InputManager.getIconFromInputName(bind);

						if (icon === undefined) {
							interactKey.text(bind ?? "");
						} else {
							interactKey.html(`<img src="${icon}" alt="${bind}"/>`);
						}
					}

					if (canInteract || (object === undefined && isAction)) {
						interactKey.addClass("active").show();
					} else {
						interactKey.removeClass("active").hide();
					}

					if (
						!object?.isObstacle ||
						!object.definition.isActivatable ||
						!object.definition.noInteractMessage
					) {
						interactMsg.show();
						if (
							player.downed &&
							(object?.isLoot ||
								(object?.isObstacle && object.definition.noInteractMessage))
						)
							interactMsg.hide();
					}
				} else {
					if (!UI_DEBUG_MODE) interactMsg.hide();
				}

				renderWeaponComparison(comparisonPayload);

				// Mobile stuff
				if (InputManager.isMobile && canInteract) {
					const weapons = UIManager.inventory.weapons;

					// Auto pickup (top 10 conditionals)
					if (
						GameConsole.getBuiltInCVar("cv_autopickup") &&
						object?.isLoot &&
						autoPickup &&
						// a spare is taken only by a tap (walking over a worse vest shouldn't fill the bag)
						!isSpare &&
						// nor what was just dropped here, until the player has walked away from it
						!autoPickupHeld(object.definition.idString, player.position) &&
						// Auto-pickup dual gun
						// Only pick up melees if no melee is equipped
						(((type !== DefinitionType.Melee ||
							weapons?.[2]?.definition.idString === "fists") && // FIXME are y'all fr
							// Only pick up guns if there's a free slot
							(type !== DefinitionType.Gun || !weapons?.[0] || !weapons?.[1]) &&
							// Don't pick up skins
							type !== DefinitionType.Skin &&
							// Don't pick up perks
							type !== DefinitionType.Perk) ||
							(type === DefinitionType.Gun &&
								weapons?.some((weapon) => {
									const definition = weapon?.definition;

									return (
										definition?.defType === DefinitionType.Gun &&
										((object?.definition === definition &&
											!definition.isDual &&
											definition.dualVariant) || // Picking up a single pistol when inventory has single pistol
											(object.definition as DualGunNarrowing | undefined)
												?.singleVariant === definition.idString)
										// Picking up dual pistols when inventory has a pistol
										// TODO implement splitting of dual guns to not lost reload later
									);
								})))
					) {
						// (that one, by its id: the server took the nearest it found, which could be another, a gun that
						// took the first gun's place, 1.06.1's audit)
						InputManager.addAction({ type: InputActions.Loot, target: object.id });
					} else if (
						// Auto open doors
						object?.isObstacle &&
						object.canInteract(player) &&
						object.definition.isDoor &&
						object.door?.offset === 0
					) {
						InputManager.addAction(InputActions.Interact);
					}
				}
			}

			// funny detonate button stuff
			const detonateKey = UIManager.ui.detonateKey;
			if (!InputManager.isMobile) {
				const boomBind: string | undefined =
					InputManager.binds.getInputsBoundToAction("explode_c4")[0];

				if (funnyDetonateButtonCache.bind !== boomBind) {
					// remember the detonate key itself, so the hint is only redrawn when the key changes
					// (it remembered the wrong variable and was redrawn on every update: dozens of layouts a second)
					funnyDetonateButtonCache.bind = boomBind;

					if (boomBind !== undefined) {
						const bindImg = InputManager.getIconFromInputName(boomBind);

						detonateKey.show();

						if (bindImg === undefined) {
							detonateKey.text(boomBind ?? "");
							if (detonateBindIcon !== undefined) {
								detonateKey.empty();
								detonateBindIcon = undefined;
							}
						} else {
							if (detonateBindIcon === undefined) {
								detonateKey
									.children()
									.add(
										(detonateBindIcon = $(
											`<img src="${bindImg}" alt=${boomBind} />`,
										)),
									);
							}

							if (detonateBindIcon.attr("src") !== bindImg) {
								detonateBindIcon.attr("src", bindImg);
							}
						}
					} else {
						detonateKey.hide();
					}
				}
			} else {
				detonateKey.hide();
			}
		};
	})();
})();
